// src/directives/smartName.ts
import type { DirectiveBinding } from 'vue'

type Mode = 'one-line' | 'multi-line' | 'middle-ellipsis' | 'auto'

interface SmartNameOptions {
  mode?: Mode
  lines?: 1 | 2 | 3
  tooltip?: boolean
  dirAuto?: boolean
  maxGraphemes?: number // 统一的后端/前端存储上限（字素簇），可选
  preferCSS?: boolean // auto 模式下更保守使用 CSS
  observeText?: boolean // 监听文本节点变更
}

type State = {
  cfg: Required<Omit<SmartNameOptions, 'maxGraphemes'>>
  original: string // 记录外部提供的原始文本
  isInternalUpdate: boolean // 渲染时改写 textContent 的内部标记
  render: () => void // 暴露给指令钩子手动重渲染
  suppressMutations: number // 用于批次内屏蔽 MutationObserver 回调
  setOptions: (opts: SmartNameOptions) => void // 仅更新配置
  cleanup: () => void // 统一清理观察器
}

type El = HTMLElement & { __smartNameState?: State }

const DEFAULTS: Required<Omit<SmartNameOptions, 'maxGraphemes'>> = {
  mode: 'auto',
  lines: 2,
  tooltip: true,
  dirAuto: true,
  preferCSS: true,
  observeText: true,
}

// 字素簇切分，避免切断 emoji/合字；无 Intl.Segmenter 时退化为 [...str]
const segmentGraphemes = (text: string): string[] => {
  try {
    // @ts-ignore
    const seg = new Intl.Segmenter(undefined, { granularity: 'grapheme' })
    return Array.from(seg.segment(text), (s) => s.segment)
  } catch {
    return Array.from(text)
  }
}

// 构建测量上下文，读取元素实际字体样式以精确测宽
const buildMeasure = (el: HTMLElement) => {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  const cs = getComputedStyle(el)
  ctx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`
  return (s: string) => ctx.measureText(s).width
}

// 根据容器像素宽度进行“中间省略”，尽量保留首尾可辨识信息
const middleEllipsisByWidth = (text: string, el: HTMLElement, reservePx = 0) => {
  const measure = buildMeasure(el)
  const max = el.clientWidth - reservePx
  const ell = '…'
  if (!text || measure(text) <= max) return text

  const g = segmentGraphemes(text)
  // 二分/双指针会更快；此处采用逐步扩展首尾的启发式
  let headCount = 1,
    tailCount = 1
  // 极窄容器保护
  if (max <= measure(ell)) return ell

  while (headCount + tailCount < g.length) {
    const candidate = g.slice(0, headCount).join('') + ell + g.slice(-tailCount).join('')
    const w = measure(candidate)
    if (w <= max) {
      // 尝试再多留一点字符（交替扩展首尾）
      if (headCount <= tailCount) headCount++
      else tailCount++
    } else {
      // 回退上一次成功值
      if (headCount <= tailCount) {
        headCount--
      } else {
        tailCount--
      }
      break
    }
  }
  headCount = Math.max(0, headCount)
  tailCount = Math.max(0, tailCount)
  return g.slice(0, headCount).join('') + ell + g.slice(-tailCount).join('')
}

const applyCssMode = (el: HTMLElement, mode: Mode, lines: number) => {
  if (el.dataset._snMode === mode && el.dataset._snLines === String(lines)) {
    return
  }
  el.dataset._snMode = mode
  el.dataset._snLines = String(lines)

  el.style.removeProperty('-webkit-line-clamp')
  el.style.removeProperty('display')
  el.style.removeProperty('-webkit-box-orient')
  el.style.removeProperty('white-space')
  el.style.removeProperty('text-overflow')
  el.style.removeProperty('overflow')
  el.style.removeProperty('word-break')
  el.style.removeProperty('overflow-wrap')
  el.style.removeProperty('line-break')

  if (mode === 'one-line') {
    // 单行省略
    el.style.display = 'block'
    el.style.whiteSpace = 'nowrap'
    el.style.overflow = 'hidden'
    el.style.textOverflow = 'ellipsis'
  } else if (mode === 'multi-line') {
    // 多行省略（2~3 行）
    el.style.display = '-webkit-box'
    el.style.overflow = 'hidden'
    el.style.whiteSpace = 'normal'
    ;(el.style as any)['-webkit-box-orient'] = 'vertical'
    ;(el.style as any)['-webkit-line-clamp'] = String(lines)
    ;(el.style as any)['text-wrap'] = 'pretty'
    el.style.lineBreak = 'auto'
    el.style.wordBreak = 'break-word'
    ;(el.style as any)['overflow-wrap'] = 'anywhere'
  }
}

// 设置 Tooltip / 无障碍信息
const applyA11y = (el: HTMLElement, fullText: string, enableTooltip: boolean) => {
  el.setAttribute('aria-label', fullText)
  if (enableTooltip) {
    el.setAttribute('title', fullText)
  } else {
    el.removeAttribute('title')
  }
}

// 核心挂载逻辑，返回可复用的状态，避免重复挂载导致循环
const mountSmartName = (el: HTMLElement, opts: SmartNameOptions): State => {
  let cfg: State['cfg'] = { ...DEFAULTS, ...opts }
  const state: State = {
    cfg,
    original: el.textContent ?? '',
    isInternalUpdate: false,
    suppressMutations: 0,
    render: () => {},
    setOptions: (next) => (cfg = state.cfg = { ...DEFAULTS, ...next }),
    cleanup: () => {},
  }

  // 实际渲染函数（不直接给观察器调用）
  const doRender = () => {
    const full = state.original
    if (cfg.dirAuto) el.setAttribute('dir', 'auto')
    if (cfg.mode === 'one-line' || cfg.mode === 'multi-line') {
      applyCssMode(el, cfg.mode, cfg.lines)
      // CSS 模式不改写文本，但仍需在最后确保 tooltip 显示原文
      applyA11y(el, full, cfg.tooltip)
      return
    }

    if (cfg.mode === 'middle-ellipsis') {
      // 中间省略会改写文本——加内部标记避免自触发
      el.style.overflow = 'hidden'
      const display = middleEllipsisByWidth(full, el)
      // 在本微任务内屏蔽 MO 回调，避免把 original 覆盖成省略文本
      state.isInternalUpdate = true
      state.suppressMutations++
      el.textContent = display
      queueMicrotask(() => {
        state.suppressMutations--
        state.isInternalUpdate = false
      })
      // 无论显示如何，tooltip 一律写“原文”
      applyA11y(el, full, cfg.tooltip)
      return
    }

    // auto 模式：先 CSS（多行），不够再中间省略
    if (cfg.preferCSS) {
      applyCssMode(el, 'multi-line', cfg.lines)
      const overflowed = el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth
      if (!overflowed) {
        // 仅 CSS 有效时也写原文到 tooltip
        applyA11y(el, full, cfg.tooltip)
        return
      }
    }
    // 退化为中间省略（需要改写文本）
    el.style.removeProperty('display')
    el.style.removeProperty('-webkit-line-clamp')
    el.style.whiteSpace = 'nowrap'
    el.style.textOverflow = 'ellipsis'
    el.style.overflow = 'hidden'
    const display = middleEllipsisByWidth(full, el)
    // 同上，抑制 MO，防止 original 被错误覆盖
    state.isInternalUpdate = true
    state.suppressMutations++
    el.textContent = display
    queueMicrotask(() => {
      state.suppressMutations--
      state.isInternalUpdate = false
    })
    // 退化后也保证 tooltip 是“原文”
    applyA11y(el, full, cfg.tooltip)
  }

  // 用 rAF 合并多次重算，避免 ResizeObserver 连环触发
  let rafId = 0
  const scheduleRender = () => {
    if (rafId) cancelAnimationFrame(rafId)
    rafId = requestAnimationFrame(() => {
      rafId = 0
      doRender()
    })
  }

  // 暴露渲染入口给指令钩子
  state.render = scheduleRender

  // ResizeObserver——尺寸变化时仅调度，不直接渲染
  const ro = new ResizeObserver(() => scheduleRender())
  ro.observe(el)

  // MutationObserver——仅当“外部真实变更文本”时更新 original，再调度
  const mo = cfg.observeText
    ? new MutationObserver(() => {
        if (state.isInternalUpdate || state.suppressMutations > 0) return
        state.original = el.textContent ?? ''
        scheduleRender()
      })
    : null

  if (mo) mo.observe(el, { childList: true, characterData: true, subtree: true })

  // 首帧后渲染（调度）
  scheduleRender()

  state.cleanup = () => {
    if (rafId) cancelAnimationFrame(rafId)
    ro.disconnect()
    mo?.disconnect()
  }

  return state
}

export default {
  mounted(el: El, binding: DirectiveBinding<SmartNameOptions>) {
    // 首次挂载，建立状态与观察器
    el.__smartNameState = mountSmartName(el, binding.value || {})
  },
  updated(el: El, binding: DirectiveBinding<SmartNameOptions>) {
    // 只更新配置并重渲染；不再“卸载-重挂载”，避免循环
    const state = el.__smartNameState
    if (state) {
      state.setOptions(binding.value || {})
      // 若外部通过 v-text 改了文本，这里不强行改 original，交由 MutationObserver 同步
      state.render()
    } else {
      el.__smartNameState = mountSmartName(el, binding.value || {})
    }
  },
  unmounted(el: El) {
    // 释放观察器
    el.__smartNameState?.cleanup()
    delete el.__smartNameState
  },
}
